import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useEventListener } from 'usehooks-ts'
import {
  getSnapThreshold,
  getWrapperOffsets,
  resolveSnap,
  shouldStartHorizontalSwipe,
  type ShownImage,
  SNAP_TRANSITION_MS,
} from './useCardImageSwipe.utils'

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

interface UseCardImageSwipeOptions {
  /** False on desktop, single-image variants, or when the host opts out. */
  enabled: boolean
  /** Resets the shown image back to the still-life when the user picks another color. */
  variantId: string | number | undefined
}

interface WrapperInlineStyle {
  transform: string
  /** Only set to 'none' while actively dragging; otherwise left unset so the
   * component's CSS `transition` (snap animation) takes over. */
  transition?: 'none'
  /** Only set while actively dragging: `will-change` is a compositor-layer
   * hint that should be transient, not applied for a swipe-enabled card's
   * entire lifetime (permanently promoting layers on a dense mobile grid). */
  willChange?: 'transform'
}

interface UseCardImageSwipeResult {
  containerRef: React.RefObject<HTMLDivElement>
  frontStyle: WrapperInlineStyle | undefined
  hoverStyle: WrapperInlineStyle | undefined
  handleLinkClickCapture: (event: ReactMouseEvent) => void
}

type GestureAxis = 'undecided' | 'horizontal' | 'vertical'

interface ActiveGesture {
  startX: number
  startY: number
  axis: GestureAxis
  width: number
}

// Stable references: usehooks-ts' useEventListener re-subscribes whenever the
// `options` identity changes, so a fresh object literal per render would
// tear down and rebuild the native listeners on every drag frame.
const PASSIVE_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true }
const ACTIVE_LISTENER_OPTIONS: AddEventListenerOptions = { passive: false }

export const useCardImageSwipe = ({
  enabled,
  variantId,
}: UseCardImageSwipeOptions): UseCardImageSwipeResult => {
  const containerRef = useRef<HTMLDivElement>(null)
  // usehooks-ts' useEventListener no-ops when the passed element ref's
  // `.current` is null, so swapping in this permanently-empty ref is how we
  // skip attaching native listeners on desktop/single-image/opted-out cards
  // without conditionally calling the hook (hooks can't be conditional).
  const disabledElementRef = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState<ShownImage>('front')
  const [drag, setDrag] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  // True from the moment a completed swipe starts animating fully off-screen
  // until the (invisible, transition-suppressed) swap back to drag 0 lands.
  const [isSettling, setIsSettling] = useState(false)
  // Set for one frame right after the swap so the repositioned off-screen
  // layer doesn't visibly animate from its old spot back to the new rest state.
  const [suppressTransition, setSuppressTransition] = useState(false)
  // Measured once so the not-yet-touched hover image can rest fully off-screen;
  // re-measured at the start of every gesture in case the viewport changed since.
  const [width, setWidth] = useState(0)

  const didSwipeRef = useRef(false)
  const gestureRef = useRef<ActiveGesture | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const pendingDragRef = useRef(0)
  const settleTimerRef = useRef<number | null>(null)
  const pendingShownRef = useRef<ShownImage>('front')
  // Invalidates an in-flight settle timeout (e.g. a variant switch mid-swipe)
  // without needing a `clearTimeout` during render.
  const settleTokenRef = useRef(0)

  const clearSettleTimer = () => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current)
      settleTimerRef.current = null
    }
  }

  // Colour change via VariantsSlider: reset synchronously during render
  // (React's documented pattern for state derived from a changed prop)
  // instead of an extra post-commit effect.
  const [trackedVariantId, setTrackedVariantId] = useState(variantId)

  if (variantId !== trackedVariantId) {
    settleTokenRef.current = settleTokenRef.current + 1
    setTrackedVariantId(variantId)
    setShown('front')
    setDrag(0)
    setIsSettling(false)
    setSuppressTransition(false)
  }

  useEffect(() => {
    return () => {
      clearSettleTimer()

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (!enabled) {
      return
    }

    const container = containerRef.current

    if (container) {
      setWidth(container.getBoundingClientRect().width)
    }
  }, [enabled])

  useEventListener(
    'touchstart',
    (event) => {
      const container = containerRef.current
      const touch = event.touches[0]

      if (!enabled || !container || !touch) {
        return
      }

      // Suppress the transition first so reconciling an in-flight settle
      // below (if any) doesn't visibly animate.
      setIsDragging(true)
      settleTokenRef.current = settleTokenRef.current + 1
      clearSettleTimer()

      if (isSettling) {
        setIsSettling(false)
        setShown(pendingShownRef.current)
        pendingDragRef.current = 0
        setDrag(0)
      }

      const measuredWidth = container.getBoundingClientRect().width

      gestureRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        axis: 'undecided',
        width: measuredWidth,
      }
      didSwipeRef.current = false
      setWidth(measuredWidth)
    },
    enabled ? containerRef : disabledElementRef,
    PASSIVE_LISTENER_OPTIONS,
  )

  useEventListener(
    'touchmove',
    (event) => {
      const gesture = gestureRef.current
      const touch = event.touches[0]

      if (!enabled || !gesture || !touch) {
        return
      }

      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY

      if (gesture.axis === 'undecided') {
        if (!shouldStartHorizontalSwipe(dx, dy)) {
          return
        }

        gesture.axis = 'horizontal'
        didSwipeRef.current = true
      }

      event.preventDefault()
      pendingDragRef.current = dx

      if (rafIdRef.current !== null) {
        return
      }

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        setDrag(pendingDragRef.current)
      })
    },
    enabled ? containerRef : disabledElementRef,
    ACTIVE_LISTENER_OPTIONS,
  )

  const endGesture = () => {
    const gesture = gestureRef.current

    gestureRef.current = null
    setIsDragging(false)

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }

    if (!enabled || gesture?.axis !== 'horizontal') {
      pendingDragRef.current = 0
      setDrag(0)

      return
    }

    const { width: gestureWidth } = gesture
    const threshold = getSnapThreshold(gestureWidth)
    const finalDrag = clamp(pendingDragRef.current, -gestureWidth, gestureWidth)
    const nextShown = resolveSnap({
      shown,
      drag: finalDrag,
      width: gestureWidth,
      threshold,
    })

    if (nextShown === shown) {
      // Below threshold: rubber-band back to the currently shown image.
      pendingDragRef.current = 0
      setDrag(0)

      return
    }

    // Past threshold: finish the drag by animating fully off-screen in the
    // drag direction (transition is active again now that isDragging=false),
    // then swap the shown image once that animation lands.
    const direction = finalDrag < 0 ? -1 : 1

    pendingShownRef.current = nextShown
    pendingDragRef.current = direction * gestureWidth
    setDrag(direction * gestureWidth)

    clearSettleTimer()
    setIsSettling(true)

    const token = settleTokenRef.current

    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null

      if (settleTokenRef.current !== token) {
        return
      }

      setIsSettling(false)
      setSuppressTransition(true)
      setShown(pendingShownRef.current)
      setDrag(0)
      pendingDragRef.current = 0

      requestAnimationFrame(() => {
        setSuppressTransition(false)
      })
    }, SNAP_TRANSITION_MS)
  }

  useEventListener(
    'touchend',
    endGesture,
    enabled ? containerRef : disabledElementRef,
    PASSIVE_LISTENER_OPTIONS,
  )
  useEventListener(
    'touchcancel',
    endGesture,
    enabled ? containerRef : disabledElementRef,
    PASSIVE_LISTENER_OPTIONS,
  )

  const handleLinkClickCapture = (event: ReactMouseEvent) => {
    if (!didSwipeRef.current) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    didSwipeRef.current = false
  }

  if (!enabled) {
    return {
      containerRef,
      frontStyle: undefined,
      hoverStyle: undefined,
      handleLinkClickCapture,
    }
  }

  const offsets = getWrapperOffsets({ shown, drag, width })
  const dragTransition: WrapperInlineStyle['transition'] =
    isDragging || suppressTransition ? 'none' : undefined
  const dragWillChange: WrapperInlineStyle['willChange'] =
    isDragging || isSettling ? 'transform' : undefined

  return {
    containerRef,
    frontStyle: {
      transform: `translateX(${offsets.front}px)`,
      transition: dragTransition,
      willChange: dragWillChange,
    },
    hoverStyle: {
      transform: `translateX(${offsets.hover}px)`,
      transition: dragTransition,
      willChange: dragWillChange,
    },
    handleLinkClickCapture,
  }
}
